import React, { useEffect, useRef, useState } from 'react';
import s from './ImageCropModal.module.css';

interface ImageCropModalProps {
    isOpen: boolean;
    imageSrc: string;
    onClose: () => void;
    onCrop: (croppedImage: string) => void;
    aspectRatio?: number;
}

interface Crop {
    x: number;
    y: number;
    width: number;
    height: number;
}

type ResizeHandle =
    | 'top-left'
    | 'top'
    | 'top-right'
    | 'left'
    | 'right'
    | 'bottom-left'
    | 'bottom'
    | 'bottom-right';

type CropShape = 'free' | 'square' | '4:3' | '16:9';

const MIN_SIZE = 30;

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
                                                                  isOpen,
                                                                  imageSrc,
                                                                  onClose,
                                                                  onCrop,
                                                                  aspectRatio,
                                                              }) => {
    const [crop, setCrop] = useState<Crop>({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
    });

    const [displaySize, setDisplaySize] = useState({
        width: 0,
        height: 0,
    });

    const [naturalSize, setNaturalSize] = useState({
        width: 0,
        height: 0,
    });

    const [cropShape, setCropShape] = useState<CropShape>(
        aspectRatio ? 'custom' as CropShape : 'free',
    );

    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);

    const [resizeHandle, setResizeHandle] =
        useState<ResizeHandle | null>(null);

    const [startMouse, setStartMouse] = useState({
        x: 0,
        y: 0,
    });

    const [startCrop, setStartCrop] = useState<Crop>({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
    });

    const imageRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    /**
     * Возвращает текущую пропорцию.
     *
     * undefined = свободная область.
     */
    const getCropRatio = (): number | undefined => {
        switch (cropShape) {
            case 'square':
                return 1;

            case '4:3':
                return 4 / 3;

            case '16:9':
                return 16 / 9;

            case 'free':
            default:
                return undefined;
        }
    };

    /**
     * Создаёт начальную область crop.
     */
    const createInitialCrop = (
        width: number,
        height: number,
        ratio?: number,
    ): Crop => {
        if (!width || !height) {
            return {
                x: 0,
                y: 0,
                width: 0,
                height: 0,
            };
        }

        /**
         * Свободный crop — всё изображение.
         */
        if (!ratio) {
            return {
                x: 0,
                y: 0,
                width,
                height,
            };
        }

        const imageRatio = width / height;

        /**
         * Изображение шире нужной пропорции.
         */
        if (imageRatio > ratio) {
            const cropHeight = height;
            const cropWidth = cropHeight * ratio;

            return {
                x: (width - cropWidth) / 2,
                y: 0,
                width: cropWidth,
                height: cropHeight,
            };
        }

        /**
         * Изображение выше нужной пропорции.
         */
        const cropWidth = width;
        const cropHeight = cropWidth / ratio;

        return {
            x: 0,
            y: (height - cropHeight) / 2,
            width: cropWidth,
            height: cropHeight,
        };
    };

    /**
     * Загружает изображение и определяет его размеры.
     */
    const initializeImage = () => {
        const image = imageRef.current;
        const container = containerRef.current;

        if (!image || !container) {
            return;
        }

        if (!image.naturalWidth || !image.naturalHeight) {
            return;
        }

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        if (!containerWidth || !containerHeight) {
            return;
        }

        const scaleX =
            containerWidth / image.naturalWidth;

        const scaleY =
            containerHeight / image.naturalHeight;

        const scale = Math.min(
            scaleX,
            scaleY,
            0.95,
        );

        const width = image.naturalWidth * scale;
        const height = image.naturalHeight * scale;

        setNaturalSize({
            width: image.naturalWidth,
            height: image.naturalHeight,
        });

        setDisplaySize({
            width,
            height,
        });

        /**
         * Если aspectRatio передан извне,
         * используем его.
         */
        const initialRatio = aspectRatio;

        setCrop(
            createInitialCrop(
                width,
                height,
                initialRatio ?? getCropRatio(),
            ),
        );
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const image = imageRef.current;

        if (!image) {
            return;
        }

        if (image.complete) {
            initializeImage();
        }

        image.onload = initializeImage;

        return () => {
            image.onload = null;
        };
    }, [
        isOpen,
        imageSrc,
        aspectRatio,
    ]);

    /**
     * Получает координаты мыши
     * относительно изображения.
     */
    const getImageMousePosition = (
        e: React.MouseEvent,
    ) => {
        const container = containerRef.current;

        if (!container) {
            return {
                x: 0,
                y: 0,
            };
        }

        const rect =
            container.getBoundingClientRect();

        const imageLeft =
            (container.clientWidth -
                displaySize.width) /
            2;

        const imageTop =
            (container.clientHeight -
                displaySize.height) /
            2;

        return {
            x:
                e.clientX -
                rect.left -
                imageLeft,

            y:
                e.clientY -
                rect.top -
                imageTop,
        };
    };

    /**
     * Ограничивает значение диапазоном.
     */
    const clamp = (
        value: number,
        min: number,
        max: number,
    ) => {
        return Math.max(
            min,
            Math.min(value, max),
        );
    };

    /**
     * Начало drag / resize.
     */
    const handleMouseDown = (
        e: React.MouseEvent,
    ) => {
        const target =
            e.target as HTMLElement;

        const handle =
            target.dataset.handle as
                | ResizeHandle
                | undefined;

        const pos =
            getImageMousePosition(e);

        /**
         * Resize.
         */
        if (handle) {
            e.preventDefault();
            e.stopPropagation();

            setIsResizing(true);
            setResizeHandle(handle);

            setStartMouse(pos);
            setStartCrop({
                ...crop,
            });

            return;
        }

        /**
         * Drag.
         */
        if (
            target.closest(
                `.${s.cropArea}`,
            )
        ) {
            e.preventDefault();

            setIsDragging(true);

            setStartMouse({
                x: pos.x - crop.x,
                y: pos.y - crop.y,
            });
        }
    };

    /**
     * Свободное изменение размера.
     */
    const resizeFree = (
        pos: {
            x: number;
            y: number;
        },
    ): Crop => {
        const dx =
            pos.x - startMouse.x;

        const dy =
            pos.y - startMouse.y;

        const start = startCrop;

        let left = start.x;
        let top = start.y;
        let right =
            start.x + start.width;
        let bottom =
            start.y + start.height;

        switch (resizeHandle) {
            case 'left':
                left = clamp(
                    start.x + dx,
                    0,
                    right - MIN_SIZE,
                );
                break;

            case 'right':
                right = clamp(
                    start.x +
                    start.width +
                    dx,
                    left + MIN_SIZE,
                    displaySize.width,
                );
                break;

            case 'top':
                top = clamp(
                    start.y + dy,
                    0,
                    bottom - MIN_SIZE,
                );
                break;

            case 'bottom':
                bottom = clamp(
                    start.y +
                    start.height +
                    dy,
                    top + MIN_SIZE,
                    displaySize.height,
                );
                break;

            case 'top-left':
                left = clamp(
                    start.x + dx,
                    0,
                    right - MIN_SIZE,
                );

                top = clamp(
                    start.y + dy,
                    0,
                    bottom - MIN_SIZE,
                );
                break;

            case 'top-right':
                right = clamp(
                    start.x +
                    start.width +
                    dx,
                    left + MIN_SIZE,
                    displaySize.width,
                );

                top = clamp(
                    start.y + dy,
                    0,
                    bottom - MIN_SIZE,
                );
                break;

            case 'bottom-left':
                left = clamp(
                    start.x + dx,
                    0,
                    right - MIN_SIZE,
                );

                bottom = clamp(
                    start.y +
                    start.height +
                    dy,
                    top + MIN_SIZE,
                    displaySize.height,
                );
                break;

            case 'bottom-right':
                right = clamp(
                    start.x +
                    start.width +
                    dx,
                    left + MIN_SIZE,
                    displaySize.width,
                );

                bottom = clamp(
                    start.y +
                    start.height +
                    dy,
                    top + MIN_SIZE,
                    displaySize.height,
                );
                break;
        }

        return {
            x: left,
            y: top,
            width: right - left,
            height: bottom - top,
        };
    };

    /**
     * Изменение размера с сохранением пропорции.
     *
     * Поддерживает все 8 ручек.
     */
    const resizeWithRatio = (
        pos: {
            x: number;
            y: number;
        },
        ratio: number,
    ): Crop => {
        const start = startCrop;

        const dx =
            pos.x - startMouse.x;

        const dy =
            pos.y - startMouse.y;

        const startRight =
            start.x + start.width;

        const startBottom =
            start.y + start.height;

        let left = start.x;
        let top = start.y;
        let right = startRight;
        let bottom = startBottom;

        const minWidth = MIN_SIZE;
        const minHeight =
            MIN_SIZE / ratio;

        /**
         * Для горизонтальных ручек
         * главным параметром является dx.
         */
        const widthFromDx = (
            value: number,
        ) => {
            return Math.max(
                minWidth,
                value,
            );
        };

        /**
         * Для вертикальных ручек
         * главным параметром является dy.
         */
        const widthFromDy = (
            value: number,
        ) => {
            return Math.max(
                minWidth,
                value * ratio,
            );
        };

        switch (resizeHandle) {
            /**
             * RIGHT
             */
            case 'right': {
                let width =
                    widthFromDx(
                        start.width + dx,
                    );

                width = Math.min(
                    width,
                    displaySize.width -
                    start.x,
                );

                const height =
                    width / ratio;

                /**
                 * Сохраняем верхний левый угол.
                 */
                if (
                    start.y + height <=
                    displaySize.height
                ) {
                    right =
                        start.x + width;

                    bottom =
                        start.y + height;
                } else {
                    const maxHeight =
                        displaySize.height -
                        start.y;

                    const maxWidth =
                        maxHeight * ratio;

                    right =
                        start.x +
                        maxWidth;

                    bottom =
                        displaySize.height;
                }

                break;
            }

            /**
             * LEFT
             */
            case 'left': {
                let width =
                    widthFromDx(
                        start.width - dx,
                    );

                width = Math.min(
                    width,
                    startRight,
                );

                const height =
                    width / ratio;

                /**
                 * Верхний левый угол
                 * двигается, нижний правый
                 * остаётся на месте.
                 */
                if (
                    start.y + height <=
                    displaySize.height
                ) {
                    left =
                        startRight - width;

                    bottom =
                        start.y + height;
                } else {
                    const maxHeight =
                        displaySize.height -
                        start.y;

                    const maxWidth =
                        maxHeight * ratio;

                    left =
                        startRight -
                        maxWidth;

                    bottom =
                        displaySize.height;
                }

                break;
            }

            /**
             * BOTTOM
             */
            case 'bottom': {
                let height =
                    Math.max(
                        minHeight,
                        start.height + dy,
                    );

                height = Math.min(
                    height,
                    displaySize.height -
                    start.y,
                );

                const width =
                    height * ratio;

                if (
                    start.x + width <=
                    displaySize.width
                ) {
                    right =
                        start.x + width;

                    bottom =
                        start.y + height;
                } else {
                    const maxWidth =
                        displaySize.width -
                        start.x;

                    const maxHeight =
                        maxWidth / ratio;

                    right =
                        displaySize.width;

                    bottom =
                        start.y + maxHeight;
                }

                break;
            }

            /**
             * TOP
             */
            case 'top': {
                let height =
                    Math.max(
                        minHeight,
                        start.height - dy,
                    );

                height = Math.min(
                    height,
                    startBottom,
                );

                const width =
                    height * ratio;

                if (
                    start.x + width <=
                    displaySize.width
                ) {
                    top =
                        startBottom -
                        height;

                    right =
                        start.x + width;
                } else {
                    const maxWidth =
                        displaySize.width -
                        start.x;

                    const maxHeight =
                        maxWidth / ratio;

                    top =
                        startBottom -
                        maxHeight;

                    right =
                        displaySize.width;
                }

                break;
            }

            /**
             * BOTTOM-RIGHT
             */
            case 'bottom-right': {
                let width =
                    Math.max(
                        minWidth,
                        start.width + dx,
                    );

                let height =
                    Math.max(
                        minHeight,
                        start.height + dy,
                    );

                /**
                 * Берём изменение,
                 * которое лучше соответствует
                 * движению мыши.
                 */
                if (
                    Math.abs(dx) >
                    Math.abs(dy * ratio)
                ) {
                    height =
                        width / ratio;
                } else {
                    width =
                        height * ratio;
                }

                width = Math.min(
                    width,
                    displaySize.width -
                    start.x,
                );

                height =
                    width / ratio;

                if (
                    start.y + height >
                    displaySize.height
                ) {
                    height =
                        displaySize.height -
                        start.y;

                    width =
                        height * ratio;
                }

                right =
                    start.x + width;

                bottom =
                    start.y + height;

                break;
            }

            /**
             * BOTTOM-LEFT
             */
            case 'bottom-left': {
                let width =
                    Math.max(
                        minWidth,
                        start.width - dx,
                    );

                let height =
                    Math.max(
                        minHeight,
                        start.height + dy,
                    );

                if (
                    Math.abs(dx) >
                    Math.abs(dy * ratio)
                ) {
                    height =
                        width / ratio;
                } else {
                    width =
                        height * ratio;
                }

                width = Math.min(
                    width,
                    startRight,
                );

                height =
                    width / ratio;

                if (
                    start.y + height >
                    displaySize.height
                ) {
                    height =
                        displaySize.height -
                        start.y;

                    width =
                        height * ratio;
                }

                left =
                    startRight - width;

                bottom =
                    start.y + height;

                break;
            }

            /**
             * TOP-RIGHT
             */
            case 'top-right': {
                let width =
                    Math.max(
                        minWidth,
                        start.width + dx,
                    );

                let height =
                    Math.max(
                        minHeight,
                        start.height - dy,
                    );

                if (
                    Math.abs(dx) >
                    Math.abs(dy * ratio)
                ) {
                    height =
                        width / ratio;
                } else {
                    width =
                        height * ratio;
                }

                width = Math.min(
                    width,
                    displaySize.width -
                    start.x,
                );

                height =
                    width / ratio;

                if (start.y + start.height - height < 0) {
                    height = start.y + start.height;
                    width = height * ratio;
                }

                right =
                    start.x + width;

                top =
                    startBottom - height;

                break;
            }

            /**
             * TOP-LEFT
             */
            case 'top-left': {
                let width =
                    Math.max(
                        minWidth,
                        start.width - dx,
                    );

                let height =
                    Math.max(
                        minHeight,
                        start.height - dy,
                    );

                if (
                    Math.abs(dx) >
                    Math.abs(dy * ratio)
                ) {
                    height =
                        width / ratio;
                } else {
                    width =
                        height * ratio;
                }

                width = Math.min(
                    width,
                    startRight,
                );

                height =
                    width / ratio;

                if (
                    height >
                    startBottom
                ) {
                    height = startBottom;
                    width = height * ratio;
                }

                left =
                    startRight - width;

                top =
                    startBottom - height;

                break;
            }
        }

        /**
         * Финальная защита.
         */
        const width =
            right - left;

        const height =
            bottom - top;

        if (width < MIN_SIZE) {
            if (
                resizeHandle === 'left' ||
                resizeHandle === 'top-left' ||
                resizeHandle === 'bottom-left'
            ) {
                left =
                    right - MIN_SIZE;
            } else {
                right =
                    left + MIN_SIZE;
            }
        }

        const finalWidth =
            right - left;

        const finalHeight =
            finalWidth / ratio;

        /**
         * Для вертикального положения
         * дополнительно корректируем crop.
         */
        if (
            resizeHandle === 'top' ||
            resizeHandle === 'top-left' ||
            resizeHandle === 'top-right'
        ) {
            top =
                bottom - finalHeight;
        } else {
            bottom =
                top + finalHeight;
        }

        /**
         * Не выходим за изображение.
         */
        left = Math.max(0, left);
        top = Math.max(0, top);

        right = Math.min(
            displaySize.width,
            right,
        );

        bottom = Math.min(
            displaySize.height,
            bottom,
        );

        /**
         * Если после ограничения справа
         * пропорция нарушилась — пересчитываем.
         */
        let finalCropWidth =
            right - left;

        let finalCropHeight =
            bottom - top;

        if (
            finalCropWidth /
            finalCropHeight !==
            ratio
        ) {
            finalCropHeight =
                finalCropWidth / ratio;

            if (
                resizeHandle === 'top' ||
                resizeHandle === 'top-left' ||
                resizeHandle === 'top-right'
            ) {
                top =
                    bottom -
                    finalCropHeight;
            } else {
                bottom =
                    top +
                    finalCropHeight;
            }
        }

        /**
         * Последняя проверка границ.
         */
        if (
            bottom >
            displaySize.height
        ) {
            bottom =
                displaySize.height;

            finalCropHeight =
                bottom - top;

            finalCropWidth =
                finalCropHeight * ratio;

            if (
                resizeHandle ===
                'left' ||
                resizeHandle ===
                'top-left' ||
                resizeHandle ===
                'bottom-left'
            ) {
                left =
                    right -
                    finalCropWidth;
            } else {
                right =
                    left +
                    finalCropWidth;
            }
        }

        if (
            right >
            displaySize.width
        ) {
            right =
                displaySize.width;

            finalCropWidth =
                right - left;

            finalCropHeight =
                finalCropWidth / ratio;

            if (
                resizeHandle === 'top' ||
                resizeHandle === 'top-left' ||
                resizeHandle === 'top-right'
            ) {
                top =
                    bottom -
                    finalCropHeight;
            } else {
                bottom =
                    top +
                    finalCropHeight;
            }
        }

        return {
            x: Math.max(0, left),
            y: Math.max(0, top),
            width: right - left,
            height: bottom - top,
        };
    };

    /**
     * Mouse move.
     */
    const handleMouseMove = (
        e: React.MouseEvent,
    ) => {
        const pos =
            getImageMousePosition(e);

        /**
         * Перемещение crop.
         */
        if (isDragging) {
            const x = clamp(
                pos.x - startMouse.x,
                0,
                displaySize.width -
                crop.width,
            );

            const y = clamp(
                pos.y - startMouse.y,
                0,
                displaySize.height -
                crop.height,
            );

            setCrop(prev => ({
                ...prev,
                x,
                y,
            }));

            return;
        }

        /**
         * Изменение размера.
         */
        if (
            isResizing &&
            resizeHandle
        ) {
            const ratio =
                getCropRatio();

            const newCrop = ratio
                ? resizeWithRatio(
                    pos,
                    ratio,
                )
                : resizeFree(pos);

            setCrop(newCrop);
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setIsResizing(false);
        setResizeHandle(null);
    };

    /**
     * Смена формы.
     */
    const changeShape = (
        shape: CropShape,
    ) => {
        setCropShape(shape);

        let ratio: number | undefined;

        switch (shape) {
            case 'square':
                ratio = 1;
                break;

            case '4:3':
                ratio = 4 / 3;
                break;

            case '16:9':
                ratio = 16 / 9;
                break;

            case 'free':
            default:
                ratio = undefined;
                break;
        }

        setCrop(
            createInitialCrop(
                displaySize.width,
                displaySize.height,
                ratio,
            ),
        );
    };

    /**
     * Сброс crop.
     */
    const handleReset = () => {
        setCrop(
            createInitialCrop(
                displaySize.width,
                displaySize.height,
                aspectRatio ??
                getCropRatio(),
            ),
        );
    };

    /**
     * Применить crop.
     */
    const handleCrop = () => {
        const canvas =
            canvasRef.current;

        const image =
            imageRef.current;

        if (!canvas || !image) {
            return;
        }

        if (
            !naturalSize.width ||
            !naturalSize.height ||
            !displaySize.width ||
            !displaySize.height ||
            !crop.width ||
            !crop.height
        ) {
            return;
        }

        const scaleX =
            naturalSize.width /
            displaySize.width;

        const scaleY =
            naturalSize.height /
            displaySize.height;

        const sourceX =
            crop.x * scaleX;

        const sourceY =
            crop.y * scaleY;

        const sourceWidth =
            crop.width * scaleX;

        const sourceHeight =
            crop.height * scaleY;

        canvas.width =
            Math.round(sourceWidth);

        canvas.height =
            Math.round(sourceHeight);

        const ctx =
            canvas.getContext('2d');

        if (!ctx) {
            return;
        }

        ctx.clearRect(
            0,
            0,
            canvas.width,
            canvas.height,
        );

        ctx.drawImage(
            image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            canvas.width,
            canvas.height,
        );

        const result =
            canvas.toDataURL(
                'image/png',
            );

        onCrop(result);
        onClose();
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div
            className={s.overlay}
            onClick={onClose}
        >
            <div
                className={s.modal}
                onClick={e =>
                    e.stopPropagation()
                }
            >
                <div className={s.header}>
                    <h3 className={s.title}>
                        Редактирование фото
                    </h3>

                    <button
                        className={s.closeBtn}
                        onClick={onClose}
                        type="button"
                    >
                        ✕
                    </button>
                </div>

                <div
                    className={s.cropContainer}
                    ref={containerRef}
                    onMouseDown={
                        handleMouseDown
                    }
                    onMouseMove={
                        handleMouseMove
                    }
                    onMouseUp={
                        handleMouseUp
                    }
                    onMouseLeave={
                        handleMouseUp
                    }
                >
                    <div
                        className={
                            s.cropWrapper
                        }
                    >
                        <img
                            ref={imageRef}
                            src={imageSrc}
                            alt="Crop"
                            className={
                                s.cropImage
                            }
                            style={{
                                width:
                                displaySize.width,
                                height:
                                displaySize.height,
                            }}
                            draggable={false}
                        />

                        {displaySize.width >
                            0 &&
                            displaySize.height >
                            0 && (
                                <div
                                    className={
                                        s.cropArea
                                    }
                                    style={{
                                        left:
                                            `calc(50% - ${displaySize.width / 2}px + ${crop.x}px)`,
                                        top:
                                            `calc(50% - ${displaySize.height / 2}px + ${crop.y}px)`,
                                        width:
                                        crop.width,
                                        height:
                                        crop.height,
                                    }}
                                >
                                    <div
                                        className={
                                            s.cropGrid
                                        }
                                    />

                                    <div
                                        className={`${s.resizeHandle} ${s.topLeft}`}
                                        data-handle="top-left"
                                    />

                                    <div
                                        className={`${s.resizeHandle} ${s.top}`}
                                        data-handle="top"
                                    />

                                    <div
                                        className={`${s.resizeHandle} ${s.topRight}`}
                                        data-handle="top-right"
                                    />

                                    <div
                                        className={`${s.resizeHandle} ${s.left}`}
                                        data-handle="left"
                                    />

                                    <div
                                        className={`${s.resizeHandle} ${s.right}`}
                                        data-handle="right"
                                    />

                                    <div
                                        className={`${s.resizeHandle} ${s.bottomLeft}`}
                                        data-handle="bottom-left"
                                    />

                                    <div
                                        className={`${s.resizeHandle} ${s.bottom}`}
                                        data-handle="bottom"
                                    />

                                    <div
                                        className={`${s.resizeHandle} ${s.bottomRight}`}
                                        data-handle="bottom-right"
                                    />
                                </div>
                            )}
                    </div>

                    <div
                        className={
                            s.shapeControls
                        }
                    >
                        <button
                            type="button"
                            className={
                                cropShape ===
                                'free'
                                    ? s.shapeButtonActive
                                    : s.shapeButton
                            }
                            onClick={() =>
                                changeShape(
                                    'free',
                                )
                            }
                        >
                            Свободная
                        </button>

                        <button
                            type="button"
                            className={
                                cropShape ===
                                'square'
                                    ? s.shapeButtonActive
                                    : s.shapeButton
                            }
                            onClick={() =>
                                changeShape(
                                    'square',
                                )
                            }
                        >
                            1:1
                        </button>

                        <button
                            type="button"
                            className={
                                cropShape ===
                                '4:3'
                                    ? s.shapeButtonActive
                                    : s.shapeButton
                            }
                            onClick={() =>
                                changeShape(
                                    '4:3',
                                )
                            }
                        >
                            4:3
                        </button>

                        <button
                            type="button"
                            className={
                                cropShape ===
                                '16:9'
                                    ? s.shapeButtonActive
                                    : s.shapeButton
                            }
                            onClick={() =>
                                changeShape(
                                    '16:9',
                                )
                            }
                        >
                            16:9
                        </button>
                    </div>

                    <div
                        className={
                            s.controls
                        }
                    >
                        <span
                            className={
                                s.controlHint
                            }
                        >
                            🖱 Перетаскивайте
                            область
                        </span>

                        <span
                            className={
                                s.controlHint
                            }
                        >
                            ↔ Тяните края и углы
                        </span>

                        <span
                            className={
                                s.sizeInfo
                            }
                        >
                            {Math.round(
                                crop.width,
                            )}{' '}
                            ×{' '}
                            {Math.round(
                                crop.height,
                            )}
                        </span>

                        <button
                            className={
                                s.resetBtn
                            }
                            onClick={
                                handleReset
                            }
                            type="button"
                        >
                            ↺ Сбросить
                        </button>
                    </div>
                </div>

                <div
                    className={s.footer}
                >
                    <button
                        className={
                            s.cancelBtn
                        }
                        onClick={onClose}
                        type="button"
                    >
                        Отмена
                    </button>

                    <button
                        className={
                            s.cropBtn
                        }
                        onClick={
                            handleCrop
                        }
                        type="button"
                    >
                        Применить
                    </button>
                </div>

                <canvas
                    ref={canvasRef}
                    style={{
                        display: 'none',
                    }}
                />
            </div>
        </div>
    );
};